const LOGIN_DIAGNOSTIC_PREFIX = "[LOGIN_DIAGNOSTIC]";
const MAX_DIAGNOSTIC_TEXT_LENGTH = 500;
const MAX_DIAGNOSTIC_STACK_LENGTH = 2_000;

const SENSITIVE_KEYS =
  "authorization|cookie|credential|proof|secret|signature|challenge|challengetoken|password|passwordconfirmation|otp|code|accesstoken|refreshtoken|access[_-]token|refresh[_-]token|public[_-]?key|private[_-]?key|token";

const BEARER_TOKEN_PATTERN = /Bearer\s+[^\s"']+/gi;
const JSON_SECRET_PATTERN = new RegExp(
  `"(${SENSITIVE_KEYS})"\\s*:\\s*("[^"]*"|[^,}\\s]+)`,
  "gi",
);
const ASSIGNED_SECRET_PATTERN = new RegExp(
  `\\b(${SENSITIVE_KEYS})\\b\\s*[:=]\\s*[^\\s,;}&]+`,
  "gi",
);
const QUERY_SECRET_PATTERN = new RegExp(
  `([?&](?:${SENSITIVE_KEYS})=)[^&#\\s]+`,
  "gi",
);

/**
 * Login step a failure belongs to, used to route a report to the right owner.
 *
 * @example
 * logLoginDiagnostic("login_failed", { stage: "trustedDeviceChallenge" });
 */
export type LoginDiagnosticStage =
  | "authRecovery"
  | "loginAction"
  | "loginCompletion"
  | "trustedDeviceCapability"
  | "trustedDeviceChallenge"
  | "trustedDeviceDatabase"
  | "trustedDeviceSignature";

/**
 * Bounded scalar payload of one diagnostic line — never a request or form body.
 *
 * @example
 * const fields: LoginDiagnosticFields = { stage: "loginAction", status: 401 };
 */
export type LoginDiagnosticFields = Record<
  string,
  string | number | boolean | null | undefined
>;

/**
 * Console severity a diagnostic line is emitted with.
 *
 * @example
 * logLoginDiagnostic("login_failed", fields, "error");
 */
export type LoginDiagnosticLevel = "error" | "info" | "warn";

/**
 * Emit one grep-friendly JSON line describing a login failure.
 *
 * Only scalar, redacted fields are serialized; callers must never pass form
 * values, credentials, tokens, or trusted-device key material.
 *
 * @example
 * logLoginDiagnostic("login_failed", { stage: "loginAction" }, "error");
 */
export function logLoginDiagnostic(
  event: string,
  fields: LoginDiagnosticFields = {},
  level: LoginDiagnosticLevel = "warn",
): void {
  const message = `${LOGIN_DIAGNOSTIC_PREFIX} ${JSON.stringify({
    timestamp: new Date().toISOString(),
    event,
    ...fields,
  })}`;

  if (level === "error") {
    console.error(message);

    return;
  }

  if (level === "info") {
    console.info(message);

    return;
  }

  console.warn(message);
}

/**
 * Extract the name, redacted message, code, status, and path of a failure.
 *
 * The request body, response body, and headers are deliberately excluded so a
 * submitted password, OTP, or issued token can never reach the console.
 *
 * @example
 * logLoginDiagnostic("login_failed", getSafeLoginErrorDiagnostic(error));
 */
export function getSafeLoginErrorDiagnostic(
  error: unknown,
): LoginDiagnosticFields {
  const errorRecord = asRecord(error);
  const responseRecord = asRecord(errorRecord?.response);
  const configRecord = asRecord(errorRecord?.config);

  return {
    errorName: redactLoginDiagnosticText(errorRecord?.name),
    errorMessage: redactLoginDiagnosticText(errorRecord?.message),
    errorCode: redactLoginDiagnosticText(errorRecord?.code),
    errorStack: redactLoginDiagnosticText(
      errorRecord?.stack,
      MAX_DIAGNOSTIC_STACK_LENGTH,
    ),
    hasResponse: responseRecord !== undefined,
    responseStatus: getSafeNumber(
      responseRecord?.status ?? errorRecord?.status,
    ),
    requestMethod: redactLoginDiagnosticText(
      configRecord?.method,
    )?.toUpperCase(),
    requestPath: getSafeRequestPath(configRecord?.url),
  };
}

/**
 * Strip credential-shaped fragments from free text and bound its length.
 *
 * @example
 * redactLoginDiagnosticText('{"password":"hunted"}'); // '{"password":"[REDACTED]"}'
 */
export function redactLoginDiagnosticText(
  value: unknown,
  maxLength: number = MAX_DIAGNOSTIC_TEXT_LENGTH,
): string | undefined {
  if (typeof value !== "string" || value.length === 0) return undefined;

  return value
    .replace(BEARER_TOKEN_PATTERN, "Bearer [REDACTED]")
    .replace(JSON_SECRET_PATTERN, '"$1":"[REDACTED]"')
    .replace(ASSIGNED_SECRET_PATTERN, "$1=[REDACTED]")
    .replace(QUERY_SECRET_PATTERN, "$1[REDACTED]")
    .slice(0, maxLength);
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
  if (!value || typeof value !== "object") return undefined;

  return value as Record<string, unknown>;
}

function getSafeNumber(value: unknown): number | undefined {
  if (typeof value !== "number" || !Number.isFinite(value)) return undefined;

  return value;
}

function getSafeRequestPath(value: unknown): string | undefined {
  if (typeof value !== "string" || value.length === 0) return undefined;

  try {
    return new URL(value, "http://diagnostic.invalid").pathname.slice(
      0,
      MAX_DIAGNOSTIC_TEXT_LENGTH,
    );
  } catch {
    return undefined;
  }
}
